/**
 * Reusable Collection Listing Hook
 *
 * Extracts the shared pagination + station-filtering logic used by
 * the podcasts and redaktion (articles) listing routes.
 *
 * Handles:
 * - Initial SSR data fetch with streaming
 * - Station tab building from channels
 * - Station filtering with server-side re-fetch
 * - Load-more pagination
 * - Derived state (user-modified data overlaying initial SSR data)
 */

import { createSignal, createMemo, Accessor } from "solid-js";
import { createAsync } from "@solidjs/router";
import { getStreamingChannelsByTenantQuery } from "~/api/cms/queries";
import { env } from "~/config/env";
import { logger } from "~/lib/logger/logger";
import { sortChannelTabsByPriority } from "~/utils/channelTabSort";
import type { CMSPaginatedResponse } from "~/api/cms/queryUtils";
import type { StationTab } from "~/components/pages";

export interface UseCollectionListingOptions<T> {
  /** Query function returning paginated results */
  queryFn: (
    tenantSlug: string,
    page: number,
    limit: number,
    station?: string
  ) => Promise<CMSPaginatedResponse<T>>;
  /** Items per page (default: 12) */
  pageSize?: number;
  /** Label for error logging (e.g. "podcasts", "articles") */
  label: string;
}

export interface UseCollectionListingResult<T> {
  items: Accessor<T[]>;
  totalItems: Accessor<number>;
  hasMorePages: Accessor<boolean>;
  stationTabs: Accessor<StationTab[]>;
  selectedStationId: Accessor<string | null>;
  isChangingStation: Accessor<boolean>;
  isLoadingMore: Accessor<boolean>;
  isDataLoaded: Accessor<boolean>;
  /** Error message when a station-change or load-more request fails */
  error: Accessor<string | null>;
  handleStationClick: (stationId: string | null) => void;
  loadMore: () => void;
}

export function useCollectionListing<T>(
  options: UseCollectionListingOptions<T>
): UseCollectionListingResult<T> {
  const { queryFn, label, pageSize = 12 } = options;

  // State for selected station tab
  const [selectedStationId, setSelectedStationId] = createSignal<string | null>(null);

  // State for pagination (load more) - only used after user interaction
  const [userItems, setUserItems] = createSignal<T[] | null>(null);
  const [userTotalItems, setUserTotalItems] = createSignal<number | null>(null);
  const [userHasMorePages, setUserHasMorePages] = createSignal<boolean | null>(null);
  const [isLoadingMore, setIsLoadingMore] = createSignal(false);
  const [isChangingStation, setIsChangingStation] = createSignal(false);
  const [errorState, setErrorState] = createSignal<string | null>(null);
  const [currentPage, setCurrentPage] = createSignal(1);

  // Fetch channels for station tabs with streaming
  const channelsData = createAsync(() => getStreamingChannelsByTenantQuery(env.tenantSlug), {
    deferStream: true,
  });

  // Fetch initial items (page 1, no station filter) with streaming
  const initialData = createAsync(() => queryFn(env.tenantSlug, 1, pageSize), {
    deferStream: true,
  });

  // Derived data: use user-modified data if available, otherwise use initial SSR data
  const items = createMemo(() => {
    const userData = userItems();
    if (userData !== null) return userData;
    return initialData()?.docs ?? [];
  });

  const totalItems = createMemo(() => {
    const userTotal = userTotalItems();
    if (userTotal !== null) return userTotal;
    return initialData()?.totalDocs ?? 0;
  });

  const hasMorePages = createMemo(() => {
    const userHasMore = userHasMorePages();
    if (userHasMore !== null) return userHasMore;
    return initialData()?.hasNextPage ?? false;
  });

  // Build station tabs array
  const stationTabs = createMemo(() => {
    const channels = channelsData();
    if (!channels || !Array.isArray(channels) || channels.length === 0) return [];

    // Filter only sender channels (stations, not pure streams), exclude null entries
    const senderChannels = channels.filter(
      (channel) => channel != null && (channel.channelType === "sender" || !channel.channelType)
    );

    const sortedChannels = sortChannelTabsByPriority(senderChannels);

    return [
      {
        id: null,
        displayName: "Alle",
        backgroundColor: "#cc1133",
      },
      ...sortedChannels
        .filter((channel) => channel.id && channel.displayName)
        .map((channel) => ({
          id: channel.id,
          displayName: channel.displayName,
          backgroundColor: channel.branding?.primaryColor || "#cc1133",
        })),
    ];
  });

  // Fetch items when station changes (server-side filtering)
  const fetchForStation = async (stationId: string | null) => {
    setIsChangingStation(true);
    setErrorState(null);
    setCurrentPage(1);

    try {
      const data = await queryFn(env.tenantSlug, 1, pageSize, stationId || undefined);

      if (data?.docs) {
        setUserItems(data.docs);
        setUserTotalItems(data.totalDocs || 0);
        setUserHasMorePages(data.hasNextPage || false);
      }
    } catch (error) {
      logger.error(`Error fetching ${label} for station:`, error);
      setErrorState(`Fehler beim Laden der ${label}`);
    } finally {
      setIsChangingStation(false);
    }
  };

  // Handle station tab click
  const handleStationClick = async (stationId: string | null) => {
    if (stationId === selectedStationId()) return;

    setSelectedStationId(stationId);
    window.scrollTo({ top: 0, behavior: "smooth" });
    await fetchForStation(stationId);
  };

  // Handle "Load More" button
  const loadMore = async () => {
    if (isLoadingMore()) return;

    setIsLoadingMore(true);
    const nextPage = currentPage() + 1;
    const stationId = selectedStationId();

    try {
      const newData = await queryFn(env.tenantSlug, nextPage, pageSize, stationId || undefined);

      if (newData?.docs) {
        const currentItems = items();
        setUserItems([...currentItems, ...newData.docs]);
        setUserHasMorePages(newData.hasNextPage || false);
        setUserTotalItems(newData.totalDocs || totalItems());
        setCurrentPage(nextPage);
      }
    } catch (error) {
      logger.error(`Error loading more ${label}:`, error);
      setErrorState(`Fehler beim Laden weiterer ${label}`);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const isDataLoaded = () => initialData() !== undefined && channelsData() !== undefined;

  return {
    items,
    totalItems,
    hasMorePages,
    stationTabs,
    selectedStationId,
    isChangingStation,
    isLoadingMore,
    isDataLoaded,
    error: errorState,
    handleStationClick,
    loadMore,
  };
}
